/**
 * A durable record of each note's change in flight (PLAN.md "Client engine":
 * a seq that left the device is never reused). The SQLite database reaches
 * IndexedDB only in whole, debounced exports, too heavy to await on every
 * submit; this small store holds just `(noteId, clientId) → {clientId, seq,
 * baseRev, ops}` (two tabs on one note keep a record each) and is awaited
 * before the change is sent. A change whose record could not be written is
 * not sent. At load, `reconcileInflightRecords` puts a recorded change back
 * into a database export that missed it, so it is resent under the same seq;
 * a record the database has moved past is dropped; a record newer than the
 * database (later seqs went out after the export) puts the note at no base.
 */

import { apply, identityOp, initialSyncState, type NoteSyncState } from "@caliu/utils";
import { Sentry } from "./sentry";

export type InflightRecord = NonNullable<NoteSyncState["inflight"]>;

/** Where records live (by `recordKey`): IndexedDB in the browser, memory where there is none (tests, SSR). */
export interface InflightBackend {
  put(key: string, rec: InflightRecord): Promise<void>;
  delete(key: string): Promise<void>;
  all(): Promise<Array<[string, InflightRecord]>>;
  clear(): Promise<void>;
}

/** One record per note and client id. */
export function recordKey(noteId: string, clientId: string): string {
  return `${noteId}\n${clientId}`;
}

function noteIdOfKey(key: string): string {
  const i = key.indexOf("\n");
  return i < 0 ? key : key.slice(0, i);
}

const IDB_NAME = "caliu-inflight";
const IDB_STORE = "changes";

function idbBackend(): InflightBackend {
  let open: Promise<IDBDatabase> | null = null;
  const db = () =>
    (open ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(IDB_STORE)) req.result.createObjectStore(IDB_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        open = null;
        reject(req.error ?? new Error("inflight store did not open"));
      };
    }));
  const run = async (mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<unknown> => {
    const idb = await db();
    return new Promise((resolve, reject) => {
      const tx = idb.transaction(IDB_STORE, mode);
      const req = fn(tx.objectStore(IDB_STORE));
      // Resolved once committed: a put is durable when this settles.
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => reject(tx.error ?? new Error("inflight store write failed"));
      tx.onabort = () => reject(tx.error ?? new Error("inflight store write aborted"));
    });
  };
  return {
    put: async (key, rec) => {
      await run("readwrite", (s) => s.put(rec, key));
    },
    delete: async (key) => {
      await run("readwrite", (s) => s.delete(key));
    },
    clear: async () => {
      await run("readwrite", (s) => s.clear());
    },
    all: async () => {
      const idb = await db();
      return new Promise((resolve, reject) => {
        const tx = idb.transaction(IDB_STORE, "readonly");
        const store = tx.objectStore(IDB_STORE);
        const keys = store.getAllKeys();
        const values = store.getAll();
        tx.oncomplete = () =>
          resolve((keys.result as string[]).map((k, i) => [k, values.result[i] as InflightRecord]));
        tx.onerror = () => reject(tx.error);
      });
    },
  };
}

export function memoryInflightBackend(): InflightBackend {
  const map = new Map<string, InflightRecord>();
  return {
    put: async (key, rec) => void map.set(key, rec),
    delete: async (key) => void map.delete(key),
    all: async () => [...map.entries()],
    clear: async () => map.clear(),
  };
}

let backend: InflightBackend | null = null;
/** Seq last made durable per record key: a resend of the same change writes nothing. */
const durable = new Map<string, number>();

function getBackend(): InflightBackend {
  backend ??= typeof indexedDB === "undefined" ? memoryInflightBackend() : idbBackend();
  return backend;
}

/** Tests: a fresh backend and no memory of what was written. */
export function setInflightBackend(b: InflightBackend | null): void {
  backend = b;
  durable.clear();
}

/** Makes the note's change in flight durable. Rejects when it is not: do not send it. */
export async function writeInflightRecord(noteId: string, rec: InflightRecord): Promise<void> {
  const key = recordKey(noteId, rec.clientId);
  if (durable.get(key) === rec.seq) return;
  await getBackend().put(key, rec);
  durable.set(key, rec.seq);
}

/** The note's change in flight under `clientId` landed (or was dropped). Best effort: a stale record is dropped at load. */
export function clearInflightRecord(noteId: string, clientId: string): void {
  const key = recordKey(noteId, clientId);
  if (!durable.has(key)) return;
  durable.delete(key);
  getBackend()
    .delete(key)
    .catch((err: unknown) => Sentry.captureException(err, { tags: { operation: "inflight_record_clear" } }));
}

/** Sign-out: every record goes with the account's database. */
export async function clearInflightRecords(): Promise<void> {
  durable.clear();
  await getBackend().clear();
}

/**
 * What to do with a record given the stored state at load: "keep" (the state
 * has it), "restore" (the state is from before it was put in flight: put it
 * back), "drop" (the state is at it or past it, or the note left the engine)
 * or "reset" (the record is newer than the state: later seqs left after the
 * debounced export, so the stored text may hold text that already landed,
 * which a catch-up would fold as foreign and the buffer would send again).
 */
export function reconcileDecision(
  s: NoteSyncState | null,
  rec: InflightRecord,
): "keep" | "restore" | "drop" | "reset" {
  if (!s || s.base === null) return "drop";
  if (s.inflight) {
    // Another client id's change: no order to compare, and the stored text
    // only holds it once it came back as a foreign change (rev moved).
    if (s.inflight.clientId !== rec.clientId) return "drop";
    if (s.inflight.seq === rec.seq) return "keep";
    return s.inflight.seq < rec.seq ? "reset" : "drop";
  }
  if (s.rev < rec.baseRev) return "reset";
  if (s.rev > rec.baseRev) return "drop";
  try {
    apply(s.base, rec.ops);
  } catch {
    return "drop";
  }
  return "restore";
}

/**
 * The state with the recorded change back in flight. The database export
 * predates the submit, and the buffer only grows until a submit, so the
 * change holds everything the stored text had: the text becomes the change's.
 */
export function restoredState(s: NoteSyncState, rec: InflightRecord): NoteSyncState {
  const content = apply(s.base as string, rec.ops);
  return { ...s, content, inflight: rec, buffer: identityOp(content.length) };
}

/**
 * The state with no base at rev 0, its text kept: the first snapshot adopts
 * the server's text, and a differing local text becomes a "Copy of …".
 */
export function resetState(s: NoteSyncState): NoteSyncState {
  return initialSyncState({ rev: 0, base: null, baseUpdatedAt: s.baseUpdatedAt, content: s.content });
}

/**
 * At load, before any sync: each record is checked against the stored state.
 * `restore` writes a state (and its text) back; it is the storage's job.
 */
export async function reconcileInflightRecords(
  load: (noteId: string) => NoteSyncState | null,
  restore: (noteId: string, prev: NoteSyncState, next: NoteSyncState) => void,
): Promise<void> {
  const b = getBackend();
  for (const [key, rec] of await b.all()) {
    const noteId = noteIdOfKey(key);
    const s = load(noteId);
    const decision = reconcileDecision(s, rec);
    if (decision === "restore" && s) {
      restore(noteId, s, restoredState(s, rec));
      Sentry.captureMessage("inflight_change_restored", { level: "info", extra: { noteId, seq: rec.seq } });
    }
    if (decision === "reset" && s) {
      restore(noteId, s, resetState(s));
      Sentry.captureMessage("inflight_change_reset", { level: "info", extra: { noteId, seq: rec.seq } });
    }
    if (decision === "drop" || decision === "reset") {
      durable.delete(key);
      await b.delete(key);
    } else durable.set(key, rec.seq);
  }
}
